import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { FaTreeCity } from "react-icons/fa6";
import { useUIStore } from '../../stores/uiStore';

export const Header: React.FC = () => {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar } = useUIStore();

  const navigation = [
    { name: 'Map', href: '/', current: location.pathname === '/' },
    { name: 'Species', href: '/species', current: location.pathname === '/species' },
    { name: 'Assessment', href: '/assess', current: location.pathname === '/assess' },
    { name: 'Community', href: '/community', current: location.pathname === '/community' },
    { name: 'About', href: '/about', current: location.pathname === '/about' },
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo and Navigation */}
          <div className="flex items-center">
            {/* Sidebar Toggle */}
            <button
              type="button"
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:ring-offset-2 lg:hidden"
              onClick={toggleSidebar}
            >
              <span className="sr-only">Toggle sidebar</span>
              {sidebarOpen ? (
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center ml-4 lg:ml-0">
              <div className="flex-shrink-0 flex items-center">
                <div className="h-8 w-8 bg-forest-600 rounded-lg flex items-center justify-center">
                  <FaTreeCity className="h-5 w-5 text-white" />
                </div>
                <div className="ml-3">
                  <h1 className="text-xl font-bold text-gray-900">Urban Forestry</h1>
                  <p className="text-xs text-gray-500">Tree Planting Platform</p>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex lg:ml-8 lg:space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200
                    ${
                      item.current
                        ? 'border-forest-500 text-forest-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center space-x-4">
            {/* Desktop Sidebar Toggle */}
            <button
              type="button"
              className="hidden lg:inline-flex p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:ring-offset-2"
              onClick={toggleSidebar}
              title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            >
              <span className="sr-only">Toggle sidebar</span>
              {sidebarOpen ? (
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden">
        <div className="pt-2 pb-3 space-y-1 border-t border-gray-200">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`
                block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200
                ${
                  item.current
                    ? 'bg-forest-50 border-forest-500 text-forest-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                }
              `}
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};
